/// <reference lib="dom" />
/** @jsx h */
import { h } from "preact";
import { useEffect } from "preact/hooks";
import { CompressorOutputDb } from "@wbaudio/utils";
import type { ChartMode } from "../../core/mode.ts";
import { useChart, useLatest } from "../../core/useChart.ts";
import { LinesLayer } from "../../layers/lines.ts";
import { type HandlePosition, HandlesLayer } from "../../layers/handles.ts";
import { SampleDomain } from "../../utils/sample.ts";
import { CreateTransferChart } from "./transferChart.ts";

export interface CompressorThresholdParam {
  value: number;
  min: number;
  max: number;
}

export interface CompressorTransferCurveProps {
  threshold: CompressorThresholdParam;
  ratio: number;
  knee: number;
  onDragThreshold?: (threshold: number) => void;
}

function CreateCompressorChart(
  container: HTMLElement,
  mode: ChartMode,
  domain: [number, number],
  onDrag: (id: string, position: HandlePosition) => void,
) {
  const curve = LinesLayer();
  const handles = mode === "editor" ? HandlesLayer({ onDrag }) : null;
  const chart = CreateTransferChart(container, {
    mode,
    domain,
    layers: [curve, ...(handles ? [handles] : [])],
  });
  return { chart, curve, handles, destroy: chart.destroy };
}

export function CompressorTransferCurve(props: CompressorTransferCurveProps) {
  const mode: ChartMode = props.onDragThreshold ? "editor" : "inline";
  const onDrag = useLatest(props.onDragThreshold);
  const { min, max, value } = props.threshold;
  const { ref, chart } = useChart(
    (container) =>
      CreateCompressorChart(
        container,
        mode,
        [min, max],
        (_, p) => onDrag.current?.(Math.round(p.x)),
      ),
    [mode, min, max],
  );

  useEffect(() => {
    if (!chart) return;
    chart.curve.set([
      {
        id: "curve",
        points: SampleDomain(
          (inputDb) => CompressorOutputDb(inputDb, value, props.ratio, props.knee),
          [min, max],
          64,
        ),
      },
    ]);
    chart.handles?.set([
      {
        id: "threshold",
        x: value,
        y: value,
        axis: "x",
        guide: "x",
        label: `${value.toFixed(0)}dB`,
        sublabel: `${props.ratio.toFixed(1)}:1`,
      },
    ]);
    chart.chart.render();
  }, [chart, value, props.ratio, props.knee]);

  return <div ref={ref} />;
}
